<template>
  <b-row>
    <b-col cols="12" md="12">
      <b-form-group
        :label="$lang.years_of_service_to_date"
        label-for="years_of_service_to_date"
        :label-cols="$config.form.labelCol"
      >
        <requiredComponent
          slot="label"
          :label="$lang.years_of_service_to_date"
          :rules="$v.toDate.$params"
        />
        <date-picker
          v-model="$v.toDate.$model"
          type="date"
          :state="$v.toDate.$dirty ? !$v.toDate.$error : null"
          inputReq="true"
          clearable
          simple
          format="YYYY-MM-DD"
          display-format="jYYYY/jMM/jDD"
          :class="
            $v.toDate.$dirty
              ? $v.toDate.$error
                ? 'invalid-date'
                : 'valid-date'
              : null
          "
        >
        </date-picker>
        <b-form-invalid-feedback
          :state="$v.toDate.$dirty ? !$v.toDate.$error : null"
        >
          <div v-if="!$v.toDate.required">
            {{ $lang.error_required | replace($lang.years_of_service_to_date) }}
          </div>
        </b-form-invalid-feedback>
      </b-form-group>
    </b-col>
    <b-col cols="12" md="12">
      <b-form-group
        :label="$lang.years_of_service_date"
        :label-cols="$config.form.labelCol"
      >
        <requiredComponent
          slot="label"
          :label="$lang.years_of_service_date"
          :rules="$v.year.$params"
        />
        <b-row>
          <b-col>
            <b-form-input
              type="number"
              autocomplete="off"
              :placeholder="$lang.year"
              v-model="$v.year.$model"
              :state="$v.year.$dirty ? !$v.year.$error : null"
            />
            <b-form-invalid-feedback
              :state="$v.year.$dirty ? !$v.year.$error : null"
            >
              <div v-if="!$v.year.required">
                {{
                  $lang.error_required
                    | replace(`${$lang.years_of_service_date} (${$lang.year})`)
                }}
              </div>
            </b-form-invalid-feedback>
          </b-col>

          <b-col>
            <b-form-input
              type="number"
              autocomplete="off"
              :placeholder="$lang.month"
              v-model="$v.month.$model"
              :state="$v.month.$dirty ? !$v.month.$error : null"
            />
            <b-form-invalid-feedback
              :state="$v.month.$dirty ? !$v.month.$error : null"
            >
              <div v-if="!$v.month.required">
                {{
                  $lang.error_required
                    | replace(`${$lang.years_of_service_date} (${$lang.month})`)
                }}
              </div>
            </b-form-invalid-feedback>
          </b-col>

          <b-col>
            <b-form-input
              type="number"
              autocomplete="off"
              :placeholder="$lang.day"
              v-model="$v.day.$model"
              :state="$v.day.$dirty ? !$v.day.$error : null"
            />
            <b-form-invalid-feedback
              :state="$v.day.$dirty ? !$v.day.$error : null"
            >
              <div v-if="!$v.day.required">
                {{
                  $lang.error_required
                    | replace(`${$lang.years_of_service_date} (${$lang.day})`)
                }}
              </div>
            </b-form-invalid-feedback>
          </b-col>
        </b-row>
      </b-form-group>
    </b-col>
    <b-col cols="12">
      <TagComponent
        category_name="person_company_job_level"
        :parentValue="jobLevel"
        :label="$lang.job_level"
        :parentPermissions="parentPermissions"
        :requiredLabel="jobLevelIsRequired"
        :checkValidation="jobLevelCheckValidation"
        :idTrack="true"
        @updateCheckValidation="jobLevelUpdateCheckValidation"
        @getSelected="updateJobLevel"
      />
    </b-col>
    <b-col cols="12">
      <TagComponent
        category_name="service_type"
        :parentValue="type"
        :label="$lang.type"
        :parentPermissions="parentPermissions"
        :requiredLabel="typeIsRequired"
        :checkValidation="typeCheckValidation"
        :idTrack="true"
        @updateCheckValidation="typeUpdateCheckValidation"
        @getSelected="updateType"
      />
    </b-col>
    <b-col cols="12" md="12" class="float-right mt-3">
      <div slot="footer" class="form-group form-btn">
        <b-button
          type="reset"
          class="mr-2"
          variant="outline-danger"
          @click="cancel"
        >
          <mdicon name="close" size="20" />
          {{ $lang.close }}
        </b-button>
        <b-button
          v-if="isAdmin(parentPermissions)"
          type="submit"
          @click="save"
          variant="success"
        >
          <mdicon name="check" size="20" />
          {{ $lang.submitButton }}
        </b-button>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import TagComponent from "@/components/tag/tagComponent.vue";
import { required, requiredIf } from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { isAdmin } from "@/shared/utils";

export default {
  components: {
    datePicker: VuePersianDatetimePicker,
    TagComponent,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    parentPermissions: {
      type: Array,
      default: () => [],
    },
  },
  validations: {
    toDate: { required },
    year: { required },
    month: {},
    day: {},
    jobLevel: { required: requiredIf((vm) => vm.jobLevelIsRequired) },
    type: { required: requiredIf((vm) => vm.typeIsRequired) },
  },
  data() {
    return {
      toDate: null,
      year: null,
      month: null,
      day: null,
      jobLevel: null,
      type: null,
      jobLevelIsRequired: false,
      jobLevelCheckValidation: false,
      typeIsRequired: false,
      typeCheckValidation: false,
    };
  },
  watch: {
    item: {
      immediate: true,
      handler(value) {
        value && this.fetchData(value);
      },
    },
  },
  methods: {
    pathOr,
    isAdmin,
    cancel() {
      this.$emit("cancel");
    },
    save() {
      this.$v.$touch();
      this.jobLevelCheckValidation = true;
      this.typeCheckValidation = true;
      if (this.jobLevelIsRequired && !this.jobLevel) return;
      if (this.typeIsRequired && !this.type) return;
      if (this.$v.$anyError) {
        return;
      }

      const payload = {
        to_date: this.toDate,
        year: this.year,
        month: this.month,
        day: this.day,
        service_type_id: pathOr(null, ["id"], this.type),
        person_company_job_level_id: pathOr(null, ["id"], this.jobLevel),
      };
      this.$emit("submit", payload);
    },
    fetchData(item) {
      this.toDate = pathOr(null, ["to_date", "gregorian"], item);
      this.year = pathOr(null, ["year"], item);
      this.month = pathOr(null, ["month"], item);
      this.day = pathOr(null, ["day"], item);
      this.jobLevel = pathOr(null, ["person_company_job_level"], item);
      this.type = pathOr(null, ["service_type"], item);
    },
    jobLevelUpdateCheckValidation(state) {
      this.jobLevelCheckValidation = state;
    },
    updateJobLevel(value) {
      this.jobLevel = value;
    },
    typeUpdateCheckValidation(state) {
      this.typeCheckValidation = state;
    },
    updateType(value) {
      this.type = value;
    },
  },
};
</script>
